import {
  CaseDto,
  DebtItemDto,
  ForecastDto,
  MultipleDto,
  PerYearValuesDto,
  ProjectDto,
} from '@app/shared/models/contracts/project-dto';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@core/store/store';
import { sumBy } from '@app/shared/helpers';
import equal from 'fast-deep-equal';
import { OpmInputFormModel2 } from '@app/modules/projects/inputs/opm-inputs-2/OpmInputs';
import {
  isBenchmarkingOnlyProject,
  setBenchmarkingCalculatedValuesToNull,
} from '@app/modules/benchmarking/benchmarking-helpers';
import { selectSlnAndPrefSharesInstrumentsWithInitialValues } from './capital-structure-slice-selectors';
import {
  CalcMethod,
  EquityBridgeColumnRole,
  ProjectCompatibilityLevel,
} from '@app/shared/models/contracts/enums/shared-enums';

const selectRawProject = (state: RootState) => state.project.project;
export const selectProjectDraft = (state: RootState) => state.project.projectDraft;
const selectProject = (state: RootState) => state.project;

export const userHasUnsavedChanges = createSelector(
  [selectRawProject, selectProjectDraft],
  (project, projectDraft) => {
    if (project.status === 'Closed') {
      return false;
    }

    // if this is not a benchmarking only project, set the calculated values to null
    // for the comparison, as we may have some projects with values set and want to
    // silently migrate them to the new structure (i.e. the user is not prompted that
    // they have changes to save, but the changes will get persisted if/when they save)
    if (!isBenchmarkingOnlyProject(projectDraft)) {
      project = setBenchmarkingCalculatedValuesToNull(project);
      projectDraft = setBenchmarkingCalculatedValuesToNull(projectDraft);
    }

    return !equal(project, projectDraft);
  }
);

export const selectSortedCasesStructure = createSelector([selectProject], (project) => {
  function mapForecasts(forecasts: ForecastDto[]) {
    return forecasts
      .slice()
      .sort((a, b) => new Date(a.forecastYear).getTime() - new Date(b.forecastYear).getTime());
  }

  return project.projectDraft.pwermInput.cases
    .map((c) => ({
      ...c,
      forecasts: mapForecasts(c.forecasts),
    }))
    .sort((a, b) => (a.caseOrder ?? 0) - (b.caseOrder ?? 0));
});

export const selectAllMultipleValues = createSelector([selectProject], (project) => {
  return [
    ...new Set(
      project.projectDraft.pwermInput.cases
        .map((x) => x.multiples.map((multiple) => Number(multiple.multiple)))
        .flat()
    ),
  ].sort((a, b) => Number(a) - Number(b));
});

export const selectOpmFormValues = createSelector(
  [selectProjectDraft, selectSlnAndPrefSharesInstrumentsWithInitialValues],
  (project, slnAndPrefSharesInstruments): OpmInputFormModel2 => {
    const opmInput = project.opmInput;
    const valuationColumn = project.equityBridge?.columns.find(
      (column) => column.role === EquityBridgeColumnRole.Valuation
    );
    const initialCashValue =
      project.details.calcMethod === CalcMethod.OPM
        ? valuationColumn?.cashValue ?? 0
        : opmInput.initialCashValue ?? 0;
    // adjust the coupon values to be displayed as percentages - this will not be required once initial coupons are moved into the Build Structure pages
    const updatedSlnAndPrefSharesInstruments = slnAndPrefSharesInstruments.map((ins) => {
      if (ins.initialValues?.coupon) {
        return {
          ...ins,
          initialValues: {
            ...ins.initialValues,
            coupon: {
              ...ins.initialValues.coupon,
              value: (ins.initialValues.coupon?.value ?? 0) * 100,
            },
          },
        };
      }

      return ins;
    });
    return {
      forecastYears: opmInput.forecastYears ?? 3,
      evValue: opmInput.evValue ?? 0,
      evVolatility: opmInput.evVolatility ?? 0,
      initialCashValue,
      netDebtItems: opmInput.netDebtItems,
      slnAndPrefSharesInstruments: updatedSlnAndPrefSharesInstruments,
      controlPremiumForDloc: opmInput.controlPremiumForDloc ?? 0,
      realWorldRate: opmInput.realWorldRate,
      valuationDateNarrative: opmInput.valuationDateNarrative,
      perYearInputs: opmInput.perYearInputs,
      initialTotalNetDebt: opmInput.initialTotalNetDebt ?? 0,
      controlPremiumForDlocByInstrument: opmInput.controlPremiumForDlocByInstrument ?? {},
      marketVolatility: opmInput.marketVolatility,
      peerSetVolatility: opmInput.peerSetVolatility,
      selectedEventSetId: opmInput.selectedEventSetId,
      selectedCaseId: opmInput.selectedCaseId,
      netDebtPerEvent: opmInput.netDebtPerEvent,
    };
  }
);

export function getCaseById(project: ProjectDto, caseId: string) {
  return project.pwermInput.cases.find((x) => x.caseId === caseId);
}

export function getMultipleById(project: ProjectDto, caseId: string, multipleId: string) {
  return getCaseById(project, caseId)?.multiples?.find((x) => x.multipleId === multipleId);
}

export const getMultipleValue = ({
  project,
  caseItem,
  multipleId,
}: {
  project: ProjectDto;
  caseItem: CaseDto;
  multipleId: string;
}) => getMultipleById(project, caseItem.caseId, multipleId)?.multiple;

export function getForecastById(project: ProjectDto, caseId: string, forecastId: string) {
  return getCaseById(project, caseId)?.forecasts.find((x) => x.forecastId === forecastId);
}

export function getMultipleProbability(
  project: ProjectDto,
  caseId: string,
  forecastId: string,
  multipleValue: number
) {
  const caseItem = getCaseById(project, caseId);
  const multiple = caseItem?.multiples.find((x) => Number(x.multiple) === Number(multipleValue));

  if (!multiple) {
    return null;
  }

  return getForecastById(project, caseId, forecastId)?.multipleProbabilities.find(
    (x) => x.multipleId === multiple.multipleId
  );
}

export const calculatePercentage = (totalAmount: number | null, partialAmount: number | null) => {
  return totalAmount && partialAmount ? (100 / totalAmount) * partialAmount : null;
};

function getForecastNetDebtItemsTotal(forecast: ForecastDto) {
  return forecast.netDebtValues.reduce((acc, item) => {
    acc = Number(acc) + Number(item.value);

    return acc;
  }, 0);
}

export function getNetDebtAtExit(forecast: ForecastDto) {
  return forecast.cashValue !== null
    ? forecast.cashValue + getForecastNetDebtItemsTotal(forecast)
    : NaN;
}

export const getTotalMultipleProbabilityPerForecast = (
  project: ProjectDto,
  caseId: string,
  forecastId: string
) => {
  const forecast = getForecastById(project, caseId, forecastId);
  return sumBy(forecast?.multipleProbabilities ?? [], (mp) => Number(mp.probability));
};

export const getMultipleIndex = (forecast: ForecastDto, multipleItem: MultipleDto) => {
  return forecast.multipleProbabilities.findIndex((multipleProbability) => {
    return multipleProbability.multipleId === multipleItem.multipleId;
  });
};

export const getNetDebtIndex = (forecast: ForecastDto, netDebtItem: DebtItemDto) => {
  return forecast.netDebtValues.findIndex((netDebtItemForecast) => {
    return netDebtItemForecast.netDebtItemId === netDebtItem.netDebtItemId;
  });
};

export const getOpmNetDebtIndex = (yearInputs: PerYearValuesDto, netDebtItem: DebtItemDto) => {
  return yearInputs.netDebtValues.findIndex((netDebtItemForecast) => {
    return netDebtItemForecast.netDebtItemId === netDebtItem.netDebtItemId;
  });
};

export const getExitMultiplesWithNonZeroProbability = (
  forecast: ForecastDto,
  caseItem: CaseDto
) => {
  const exitMultipleIdsWithNonZeroProbability = forecast.multipleProbabilities
    .filter((mp) => mp.probability! > 0)
    .map((multiple) => multiple.multipleId!);
  return caseItem.multiples
    .filter((mutliple) => exitMultipleIdsWithNonZeroProbability.includes(mutliple.multipleId))
    .map((multiple) => multiple.multiple!);
};

export const selectIsCompatibilityMode = createSelector(
  [selectProjectDraft],
  (projectDraft) => projectDraft.compatibilityLevel === ProjectCompatibilityLevel.Version_3_9
);
